import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ApiClient } from '@/infrastructure/api/api-client';
import { UnsavedChangesBar } from '@/shared/ui/unsaved-changes-bar';
import { useAuthStore } from '@/features/auth/stores/auth-store';

const ALL_MODELS = [
  // OpenAI
  { id: 'gpt-4o-mini', label: 'GPT-4o Mini', desc: 'Fast & cheap' },
  { id: 'gpt-4o', label: 'GPT-4o', desc: 'Standard' },
  { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', desc: 'Latest fast' },
  { id: 'gpt-4.1', label: 'GPT-4.1', desc: 'Latest standard' },
  { id: 'o3-mini', label: 'o3 Mini', desc: 'Reasoning' },
  { id: 'o4-mini', label: 'o4 Mini', desc: 'Advanced reasoning' },
  { id: 'gpt-5', label: 'GPT-5', desc: 'Previous intelligent reasoning model' },
  { id: 'gpt-5-mini', label: 'GPT-5 mini', desc: 'Near-frontier intelligence for cost sensitive workloads' },
  { id: 'gpt-5-nano', label: 'GPT-5 nano', desc: 'Fastest, most cost-efficient version of GPT-5' },
  { id: 'gpt-5.4', label: 'GPT-5.4', desc: 'Best intelligence at scale for agentic workflows' },
  { id: 'gpt-5.4-pro', label: 'GPT-5.4 pro', desc: 'Smarter and more precise responses' },
  { id: 'gpt-5.4-mini', label: 'GPT-5.4 mini', desc: 'Our strongest mini model yet' },
  { id: 'gpt-5.4-nano', label: 'GPT-5.4 nano', desc: 'Cheapest GPT-5.4-class model' },
  // Anthropic
  { id: 'claude-opus-5', label: 'Claude Opus 5', desc: 'Anthropic top-tier reasoning' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', desc: 'Anthropic balanced default' },
  { id: 'claude-fable-5', label: 'Claude Fable 5', desc: 'Anthropic fast tier' },
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7', desc: 'Previous generation' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', desc: 'Previous generation' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', desc: 'Anthropic fast & cheap' },
  // Google Gemini
  { id: 'gemini-3.1-pro-high', label: 'Gemini 3.1 Pro (High)', desc: 'Pro with dynamic thinking budget' },
  { id: 'gemini-3.1-pro-low', label: 'Gemini 3.1 Pro (Low)', desc: 'Pro with capped thinking budget' },
  { id: 'gemini-3-flash', label: 'Gemini 3 Flash', desc: 'Fast multimodal model' },
];

interface AdminSettings {
  openai_api_key: string | null;
  openai_api_key_set: boolean;
  wavespeed_api_key: string | null;
  wavespeed_api_key_set: boolean;
  pexels_api_key: string | null;
  pexels_api_key_set: boolean;
  allow_user_api_keys: boolean;
  user_key_allowed_models: string[] | null;
  heygen_api_key: string | null;
  heygen_api_key_set: boolean;
  json2video_api_key: string | null;
  json2video_api_key_set: boolean;
  anthropic_api_key: string | null;
  anthropic_api_key_set: boolean;
  claude_subscription_tokens: string | null;
  claude_subscription_tokens_set: boolean;
  claude_subscription_url: string | null;
  claude_subscription_secret_set: boolean;
  anthropic_mode: 'api_key' | 'subscription';
  gemini_api_key: string | null;
  gemini_api_key_set: boolean;
}

export const Route = createFileRoute('/admin/settings')({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (!user?.is_admin) throw new Error('Admin access required');
  },
  component: SettingsPage,
});

function SettingsPage() {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowUserKeys, setAllowUserKeys] = useState(true);
  const [userKeyModels, setUserKeyModels] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    ApiClient.get<AdminSettings>('/admin/settings')
      .then((data) => {
        setSettings(data);
        setAllowUserKeys(data.allow_user_api_keys);
        setUserKeyModels(data.user_key_allowed_models ?? ALL_MODELS.map((m) => m.id));
      })
      .finally(() => setLoading(false));
  }, []);

  const dirty =
    allowUserKeys !== (settings?.allow_user_api_keys ?? true)
    || JSON.stringify([...userKeyModels].sort())
       !== JSON.stringify([...(settings?.user_key_allowed_models ?? [])].sort());

  const discard = () => {
    setAllowUserKeys(settings?.allow_user_api_keys ?? true);
    setUserKeyModels(settings?.user_key_allowed_models ?? []);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        allow_user_api_keys: allowUserKeys,
        user_key_allowed_models: userKeyModels,
      };

      await ApiClient.put('/admin/settings', payload);

      // Reload settings
      const updated = await ApiClient.get<AdminSettings>('/admin/settings');
      setSettings(updated);
      setMessage('Settings saved successfully');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="mb-6 text-2xl font-bold text-white">Settings</h1>

      <div className="max-w-2xl space-y-6">
        {message && (
          <div className="rounded-lg border border-green-800 bg-green-950 p-3 text-sm text-green-300">
            {message}
          </div>
        )}

        {/* Allow User API Keys */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
          <label className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Allow User API Keys
              </h2>
              <p className="text-sm text-zinc-400">
                Global switch. When off, no user can use their own API key
                regardless of plan settings.
              </p>
            </div>
            <div
              onClick={() => setAllowUserKeys(!allowUserKeys)}
              className={`relative h-6 w-11 cursor-pointer rounded-full transition-colors ${allowUserKeys ? 'bg-blue-600' : 'bg-zinc-700'
                }`}
            >
              <div
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${allowUserKeys ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
              />
            </div>
          </label>
        </div>

        {/* Models allowed when using own key */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-1 text-lg font-semibold text-white">
            Models for Own API Key
          </h2>
          <p className="mb-4 text-sm text-zinc-400">
            Which models users can select when using their own OpenAI API key.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ALL_MODELS.map((model) => (
              <label
                key={model.id}
                className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 cursor-pointer hover:border-zinc-600"
              >
                <input
                  type="checkbox"
                  checked={userKeyModels.includes(model.id)}
                  onChange={(e) =>
                    setUserKeyModels((prev) =>
                      e.target.checked
                        ? [...prev, model.id]
                        : prev.filter((m) => m !== model.id)
                    )
                  }
                  className="rounded border-zinc-600 accent-blue-500"
                />
                <div>
                  <div className="text-sm font-medium text-white">{model.label}</div>
                  <div className="text-xs text-zinc-500">{model.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      <UnsavedChangesBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={discard}
      />
    </div>
  );
}
